import { INSURABLE_GROUPS } from '@/features/quotes/data/group-life/insurable-groups';
import { Button, Dialog, Tag, Text } from '@noorden-insurtech/alba-design-system';
import { User } from 'lucide-react';
import type { PmeLifeRow } from '../../proposal.mock';

const DASH = '—';

function sexLabel(sex?: string): string {
  if (sex === 'M') return 'Masculino';
  if (sex === 'F') return 'Feminino';
  return DASH;
}

function insurableGroupLabel(value?: string): string {
  if (!value) return DASH;
  return INSURABLE_GROUPS.find(g => g.value === value)?.label ?? value;
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-1'>
      <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
        {label}
      </Text>
      <Text className='font-montserrat text-sm text-alba-gray-600'>{value}</Text>
    </div>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  life: PmeLifeRow | null;
};

export function LifeDetailsModal({ isOpen, onClose, life }: Props) {
  const cids = (life?.cids ?? []).filter(c => c.trim().length > 0);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='w-full max-w-lg'>
            <Dialog.Header>
              <Dialog.Title>
                <div className='flex items-center gap-2'>
                  <User className='h-5 w-5 text-alba-black-500' />
                  Detalhes da Vida
                </div>
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Dialog.CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <DetailField label='Nome' value={life?.name || DASH} />
                <DetailField label='CPF' value={life?.cpf || DASH} />
                <DetailField
                  label='Data de Nascimento'
                  value={life?.birthDate || DASH}
                />
                <DetailField
                  label='Sexo'
                  value={sexLabel(life?.gender || life?.sex)}
                />
                <DetailField
                  label='Grupo Segurável'
                  value={insurableGroupLabel(life?.insurableGroup)}
                />
                <DetailField label='Capital' value={life?.capital || DASH} />
                <div className='flex flex-col gap-1'>
                  <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
                    Afastado
                  </Text>
                  <div>
                    <Tag.Root
                      colorScheme={life?.awayFromWork ? 'yellow' : 'success'}
                      size='sm'
                    >
                      <Tag.Label>{life?.awayFromWork ? 'Sim' : 'Não'}</Tag.Label>
                    </Tag.Root>
                  </div>
                </div>
                {life?.awayFromWork && (
                  <DetailField
                    label='CIDs'
                    value={cids.length > 0 ? cids.join(', ') : DASH}
                  />
                )}
              </div>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant='primary' size='md' onClick={onClose}>
                Fechar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
